import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from '../../components/ui/badge';
import {
  MessageSquare,
  Bell,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Phone,
  ExternalLink,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import EmployeeMessagesTab from '../../components/EmployeeMessagesTab';
import DispatchSettingsTab from '../../components/DispatchSettingsTab';
import MarketingListsTab from '../../components/MarketingListsTab';
import InboundSmsTab from './InboundSmsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICON = {
  SMS: MessageSquare,
  EMAIL: Bell,
};

const CommunicationSettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('customer');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/communication/events`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load communication events');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div data-testid="comm-settings-loading" className="text-[#A3A3A3]">Loading…</div>;
  }
  if (!data) {
    return <div data-testid="comm-settings-error" className="text-[#A3A3A3]">No communication data available.</div>;
  }

  const events = data.events || [];

  return (
    <div data-testid="communication-settings" className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-[#D4AF37]" /> Communication Settings
        </h2>
        <p className="text-[#A3A3A3] max-w-3xl">
          Manage customer-facing messages and employee reminders. SMS and in-app channels are independently
          configurable on each tab.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="comm-tabs" className="bg-[#141414] border border-[#262626]">
          <TabsTrigger value="customer" data-testid="comm-tab-customer">Customer Messages</TabsTrigger>
          <TabsTrigger value="employee" data-testid="comm-tab-employee">Employee Messages</TabsTrigger>
          <TabsTrigger value="dispatch" data-testid="comm-tab-dispatch">Dispatch</TabsTrigger>
          <TabsTrigger value="inbound" data-testid="comm-tab-inbound">Inbound Replies</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="comm-tab-marketing">Marketing Lists</TabsTrigger>
        </TabsList>

        <TabsContent value="customer" className="mt-6 space-y-8">
          {/* Status strip */}
          <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
          <p className="text-[#525252] text-xs uppercase tracking-wide">Twilio Connection</p>
          <div className="mt-2 flex items-center gap-2">
            {data.twilio_configured ? (
              <Badge data-testid="comm-twilio-status" className="bg-green-500/15 text-green-400 border-green-700">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Configured
              </Badge>
            ) : (
              <Badge data-testid="comm-twilio-status" className="bg-amber-500/15 text-amber-400 border-amber-700">
                <AlertTriangle className="w-3 h-3 mr-1" /> Not configured
              </Badge>
            )}
          </div>
          <p className="text-[#525252] text-xs mt-2 flex items-center gap-1.5">
            <Phone className="w-3 h-3" /> From:{' '}
            <span data-testid="comm-twilio-from" className="text-white font-mono">{data.twilio_from || '—'}</span>
          </p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
          <p className="text-[#525252] text-xs uppercase tracking-wide">Global SMS Switch</p>
          <div className="mt-2">
            {data.global_sms_enabled ? (
              <Badge data-testid="comm-global-sms" className="bg-green-500/15 text-green-400 border-green-700">SMS ENABLED</Badge>
            ) : (
              <Badge data-testid="comm-global-sms" className="bg-red-500/15 text-red-400 border-red-700">SMS DISABLED</Badge>
            )}
          </div>
          <p className="text-[#525252] text-[10px] mt-2">
            Controlled by an admin under <span className="text-[#A3A3A3]">SMS / Twilio</span> settings.
          </p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
          <p className="text-[#525252] text-xs uppercase tracking-wide">Google Review Link</p>
          <a
            data-testid="comm-review-link"
            href={data.google_review_link}
            target="_blank"
            rel="noreferrer"
            className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-mono inline-flex items-center gap-1 mt-2 break-all"
          >
            {data.google_review_link}
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        </div>
      </div>

      {/* Events list */}
      <div className="space-y-4">
        <h3 className="font-['Outfit'] text-xl font-semibold text-white">Active Communication Events</h3>
        <div className="grid gap-4">
          {events.map((ev) => {
            const Icon = CATEGORY_ICON[ev.category] || MessageSquare;
            return (
              <div
                key={ev.key}
                data-testid={`comm-event-${ev.key}`}
                className="bg-[#141414] border border-[#262626] rounded-lg p-5 space-y-4"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white font-semibold text-lg">{ev.label}</h4>
                        <Badge className="bg-[#1f1f1f] border-[#333] text-[#A3A3A3] text-[10px] uppercase">
                          {ev.category}
                        </Badge>
                        {!ev.transactional && (
                          <Badge className="bg-amber-500/10 text-amber-300 border-amber-700 text-[10px] uppercase">
                            Honors opt-out
                          </Badge>
                        )}
                      </div>
                      <p className="text-[#A3A3A3] text-sm mt-1">{ev.trigger}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {ev.enabled ? (
                      <Badge
                        data-testid={`comm-event-status-${ev.key}`}
                        className="bg-green-500/15 text-green-400 border-green-700"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Enabled
                      </Badge>
                    ) : (
                      <Badge
                        data-testid={`comm-event-status-${ev.key}`}
                        className="bg-red-500/15 text-red-400 border-red-700"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" /> Disabled
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center gap-2 text-[#A3A3A3]">
                    <Clock className="w-3.5 h-3.5 text-[#525252]" />
                    <span className="text-[#525252] uppercase tracking-wide">Delay:</span>
                    <span className="text-white">{ev.delay}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#A3A3A3]">
                    <Info className="w-3.5 h-3.5 text-[#525252]" />
                    <span className="text-[#525252] uppercase tracking-wide">Audience:</span>
                    <span className="text-white">{ev.audience}</span>
                  </div>
                </div>

                <div>
                  <p className="text-[#525252] text-[11px] uppercase tracking-wide mb-1.5">
                    SMS Preview (exact text customers receive)
                  </p>
                  <div
                    data-testid={`comm-event-preview-${ev.key}`}
                    className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4 text-white text-sm leading-relaxed whitespace-pre-wrap font-['Outfit']"
                  >
                    {ev.preview}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-[#0f0f0f] border border-[#262626] rounded-md p-4 text-[#A3A3A3] text-xs flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 text-[#D4AF37] flex-shrink-0" />
        <p>
          Templates are stored in code today and not yet editable. Once NexFlow is stable post-launch, this page
          will expand into a full template editor with variables, preview, enable/disable, and audit history.
        </p>
      </div>
        </TabsContent>

        <TabsContent value="employee" className="mt-6">
          <EmployeeMessagesTab />
        </TabsContent>

        <TabsContent value="dispatch" className="mt-6">
          <DispatchSettingsTab />
        </TabsContent>

        <TabsContent value="inbound" className="mt-6">
          <InboundSmsTab />
        </TabsContent>

        <TabsContent value="marketing" className="mt-6">
          <MarketingListsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommunicationSettings;
